import { createAction, createSlice, PayloadAction } from '@reduxjs/toolkit';
import { setIn } from 'utilitify';

import { IChat, IChatState, IMessage, IReassignModel } from '../types';
import { messageSorting } from '../utils';
import { CallState } from '../call/types';

const initialState: IChatState = {
  isLoading: false,
  isEnabled: false,
  searchIsLoading: false,
  chats: [],
  foundedChats: [],
  activeChatId: undefined,
  currentChat: undefined,
  lastMessageId: 0,
  unreadMessagesCount: 0,
  activeCall: undefined,
  isOpen: false,
  microphoneEnabled: true,
  cameraEnabled: true,
  soundEnabled: true,
  callSignalingData: [],
  callIsPending: true,
  isClientMuted: false,
  isClientUnAuthorized: true,
  availableLeads: undefined,
};

export const STORE_PREFIX = '@chats';

const store = createSlice({
  name: STORE_PREFIX,
  initialState,
  reducers: {
    setIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.isLoading = payload;
    },
    disable(state) {
      state.isEnabled = false;
    },
    enable(state) {
      state.isEnabled = true;
    },
    open(state) {
      state.isOpen = true;
    },
    clientUnAuthorized(
      state,
      { payload }: PayloadAction<{ isAuthorized: boolean; message?: string }>
    ) {
      state.isClientUnAuthorized = payload.isAuthorized;
    },
    cameraToggle(state, { payload }: PayloadAction<boolean>) {
      state.cameraEnabled = payload;
    },
    microphoneToggle(state, { payload }: PayloadAction<boolean>) {
      state.microphoneEnabled = payload;
    },
    soundToggle(state, { payload }: PayloadAction<boolean>) {
      state.soundEnabled = payload;
    },
    close(state) {
      state.isOpen = false;
      state.activeChatId = undefined;
      state.currentChat = undefined;
    },
    setSearchIsLoading(state, { payload }: PayloadAction<boolean>) {
      state.searchIsLoading = payload;
    },
    setChats(state, { payload }: PayloadAction<IChat[]>) {
      state.chats = payload;
    },
    setCurrentChats(state, { payload }: PayloadAction<IChat>) {
      if (payload) {
        state.currentChat = {
          ...payload,
          msgs: [...payload.msgs].sort(messageSorting),
        };

        if (payload.lastMessageId) {
          state.lastMessageId = payload.lastMessageId;
        }
      }

      state.currentChat = payload;
    },
    setCurrentChatMessages(state, { payload }: PayloadAction<IChat>) {
      if (state.currentChat) {
        state.currentChat.msgs = [
          ...state.currentChat.msgs,
          ...payload.msgs,
        ].sort(messageSorting);
      }

      state.lastMessageId = payload.lastMessageId;
    },
    setActiveChatId(state, { payload }: PayloadAction<number | undefined>) {
      state.activeChatId = payload;

      if (payload !== undefined && payload <= 0) {
        state.currentChat = state.foundedChats.length
          ? state.foundedChats.find((e: IChat) => e.id === payload)
          : undefined;
      }
    },
    setActiveChat(state, { payload }: PayloadAction<IChat | undefined>) {
      state.activeChatId = payload?.id;

      if (payload !== undefined && payload.id <= 0) {
        state.currentChat = state.foundedChats.length
          ? state.foundedChats.find((e: IChat) => e.id === payload.id)
          : undefined;
      } else {
        state.currentChat = payload;
      }
    },
    setFoundedChats(state, { payload }: PayloadAction<IChat[] | []>) {
      state.foundedChats = payload;
    },
    setLastMessageId(state, { payload }: PayloadAction<number>) {
      state.lastMessageId = payload;
    },
    setUnreadMessageCount(state, { payload }: PayloadAction<number>) {
      state.unreadMessagesCount = payload;
    },
    callState(state, { payload }: PayloadAction<CallState>) {
      state.activeCall = payload;
    },
    addCallSignalingData(
      state,
      { payload }: PayloadAction<{ '@type': string; [key: string]: any }>
    ) {
      state.callSignalingData = [...state.callSignalingData, payload];
    },
    connectionStateChange(state, { payload }: PayloadAction<string>) {
      return setIn(state, payload, 'activeCall.connectionState');
    },
    startCall: {
      reducer: (
        state,
        _: PayloadAction<{
          clientId: number;
          phoneIndex: number;
          channelId: number;
        }>
      ) => {
        state.activeCall = undefined;

        state.callIsPending = true;
        state.callSignalingData = [];
      },
      prepare: (clientId: number, phoneIndex: number, channelId: number) => ({
        payload: { clientId, phoneIndex, channelId },
      }),
    },
    endCall(state) {
      state.activeCall = undefined;
      state.callSignalingData = [];
      state.callIsPending = false;
    },
    searchChat(state, _: PayloadAction<number | undefined>) {
      state.isOpen = true;
      state.activeChatId = undefined;
      state.currentChat = undefined;
      state.foundedChats = [];
      state.isClientUnAuthorized = true;
    },
    clearSearch(state) {
      state.foundedChats = [];
      state.isClientUnAuthorized = true;
    },
    logout() {
      return initialState;
    },
    setCallIsPending(state, { payload }: PayloadAction<boolean>) {
      state.callIsPending = payload;
    },
    setIsClientMuted(state, { payload }: PayloadAction<boolean>) {
      state.isClientMuted = payload;
    },
    updateMessage(state, { payload }: PayloadAction<IMessage>) {
      if (state.currentChat && state.currentChat.id === payload.chatId) {
        if (state.currentChat.msgs.some((msg) => msg.id === payload.id)) {
          state.currentChat.msgs = state.currentChat.msgs.map((e) =>
            e.id === payload.id ? payload : e
          );
        } else {
          state.currentChat.msgs = [...state.currentChat.msgs, payload].sort(
            messageSorting
          );
        }
      }

      if (state.chats.some((e) => e.lastMessage?.id === payload.id)) {
        state.chats = state.chats.map((e) => ({
          ...e,
          lastMessage:
            e.lastMessage?.id === payload.id ? payload : e.lastMessage,
        }));
      }
    },
    addMessage(state, { payload }: PayloadAction<IMessage>) {
      state.chats = state.chats.map((e) =>
        e.id === payload.chatId
          ? {
              ...e,
              lastMessage: payload,
              count: e.count + 1,
            }
          : e
      );

      if (state.currentChat && state.currentChat.id === payload.chatId)
        state.currentChat.msgs = [...state.currentChat.msgs, payload].sort(
          messageSorting
        );
    },
    updateCurrentChatLastReadMessageId(
      state,
      { payload }: PayloadAction<IMessage>
    ) {
      if (state.currentChat && state.currentChat.id === payload.chatId)
        state.currentChat.lastReadMessageId = payload.id;

      if (state.chats)
        state.chats = state.chats.map((chat) =>
          chat.id === payload.chatId
            ? { ...chat, lastReadMessageId: payload.id }
            : chat
        );
    },
    setAvailableLeads(state, { payload }: PayloadAction<number>) {
      state.availableLeads = state.availableLeads
        ? [...state.availableLeads, payload]
        : [payload];
    },
    chatReassign(state, { payload }: PayloadAction<IReassignModel>) {
      if (state.currentChat && state.currentChat.id === payload.id)
        state.currentChat.clientId = payload.leadId;

      if (state.chats)
        state.chats = state.chats.map((chat) =>
          chat.id === payload.id ? { ...chat, clientId: payload.leadId } : chat
        );
    },
  },
});

const LOGIN_MANAGER = `${STORE_PREFIX}/INIT` as const;
const INIT_CHATS_DATA = `${STORE_PREFIX}/INIT_CHATS` as const;
const INIT_CREATED_CHAT = `${STORE_PREFIX}/INIT_CHAT` as const;
const GET_CHAT_MESSAGES = `${STORE_PREFIX}/GET_CHAT_MESSAGES` as const;
const SEND_MESSAGE = `${STORE_PREFIX}/SEND_MESSAGE` as const;
const DISCARD_CALL = `${STORE_PREFIX}/DISCARD_CALL` as const;
const CHAT_REFRESH = `${STORE_PREFIX}/CHAT_REFRESH` as const;
const CHECK_LEAD_CHAT_BUTTON_ENABLE =
  `${STORE_PREFIX}/CHECK_LEAD_CHAT_BUTTON_ENABLE` as const;

const CROUP_CONTACT_IMPORT = `${STORE_PREFIX}/CROUP_CONTACT_IMPORT` as const;

const CALL_SEND_SDP = `${STORE_PREFIX}/callSendSdp` as const;
const CALL_ON_ICE_CANDIDATE = `${STORE_PREFIX}/onIceCandidate` as const;
const CLIENT_MUTE_CHANGED = `${STORE_PREFIX}/CLIENT_MUTE_CHANGED` as const;

export const chatActions = {
  ...store.actions,
  init: createAction(LOGIN_MANAGER),
  initChats: createAction(INIT_CHATS_DATA),
  initCreatedChat: createAction<IChat>(INIT_CREATED_CHAT),
  getChatMessages: createAction<number>(GET_CHAT_MESSAGES),
  sendMessage: createAction(SEND_MESSAGE),
  discardCall: createAction(DISCARD_CALL),
  refreshChat: createAction(CHAT_REFRESH),
  sendSdp: createAction<RTCSessionDescriptionInit>(CALL_SEND_SDP),
  onIceCandidate: createAction<RTCPeerConnectionIceEvent>(
    CALL_ON_ICE_CANDIDATE
  ),
  changeClientMute: createAction<boolean>(CLIENT_MUTE_CHANGED),
  groupContactImport: createAction<{ gridName: string }>(CROUP_CONTACT_IMPORT),
  checkLeadChatButtonEnable: createAction<number>(
    CHECK_LEAD_CHAT_BUTTON_ENABLE
  ),
};

export const chatReducer = store.reducer;
